import React, { createContext, useState, useContext, useEffect } from 'react';
import api, { authenticateTelegram, setAuthToken, removeAuthToken, getAuthToken, pingActivity } from '../services/api';
import telegramService from '../services/telegram';

const AuthContext = createContext();

// Как часто отмечаемся, пока приложение открыто. Окно «в сети» на сервере —
// 5 минут, так что пропуск одного-двух тактов человека из счёта не выкинет
const PING_INTERVAL_MS = 60 * 1000;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  // Login through Telegram
  const login = async () => {
    try {
      setLoading(true);

      const telegramWebApp = telegramService.getTelegramWebApp();

      // Get initData for validation
      const initData = telegramWebApp.initData;

      if (!initData) {
        throw new Error('No Telegram initData available');
      }

      // Send initData for validation and authentication
      const { jwt, user: userData } = await authenticateTelegram(initData);

      // Save JWT using api.js helper
      setAuthToken(jwt);

      // Update state
      setUser(userData);
      setIsAuthenticated(true);

      return userData;
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Logout
  const logout = () => {
    removeAuthToken();
    setUser(null);
    setIsAuthenticated(false);
  };

  // Check existing authentication
  const checkAuth = async () => {
    try {
      const jwt = getAuthToken();

      if (!jwt) {
        setLoading(false);
        return;
      }

      // Fetch user data (JWT is auto-injected by api interceptor)
      const response = await api.get('/users/me?fields[0]=id&fields[1]=firstName&fields[2]=lastName&fields[3]=username&fields[4]=telegramUsername&fields[5]=photoUrl&fields[6]=telegramId&fields[7]=registeredAt');
      setUser(response.data);
      setIsAuthenticated(true);
    } catch (error) {
      logout();
    } finally {
      setLoading(false);
    }
  };

  // Auto-login on mount
  useEffect(() => {
    const initAuth = async () => {
      try {
        // Ждем готовности Telegram SDK
        await telegramService.waitForReady();

        // Get current Telegram user
        const currentTelegramUser = telegramService.getTelegramUser();

        // Check if we have stored JWT
        const storedJwt = getAuthToken();

        if (storedJwt && currentTelegramUser?.id) {
          // Verify stored session matches current Telegram user
          try {
            const response = await api.get('/users/me?fields[0]=id&fields[1]=firstName&fields[2]=lastName&fields[3]=username&fields[4]=telegramUsername&fields[5]=photoUrl&fields[6]=telegramId&fields[7]=registeredAt');
            const storedUser = response.data;

            // If Telegram user ID doesn't match stored user, clear old session
            if (storedUser.telegramId !== currentTelegramUser.id) {
              logout();
              await login();
            } else {
              // Session is valid for current user
              setUser(storedUser);
              setIsAuthenticated(true);
            }
          } catch (error) {
            // Token invalid or expired, login with new data
            logout();
            if (currentTelegramUser?.id) {
              await login();
            }
          }
        } else if (currentTelegramUser?.id) {
          // No stored JWT, auto-login with Telegram data
          await login();
        } else if (storedJwt) {
          // Have JWT but no Telegram user (dev mode?), try to restore
          await checkAuth();
        }
      } catch (error) {
        // Handle initialization error silently
      } finally {
        // Убираем loading только после завершения авторизации
        setLoading(false);
      }
    };

    initAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Отметка активности: сразу после авторизации и раз в минуту, пока
   * приложение на экране.
   *
   * Только при видимой вкладке — иначе свёрнутое приложение продолжало бы
   * числиться в сети и счётчик «сейчас онлайн» врал бы. По этой же причине
   * пингуем при возврате на экран, не дожидаясь следующего такта.
   */
  useEffect(() => {
    if (!isAuthenticated) return undefined;

    const beat = () => {
      if (document.visibilityState === 'visible') pingActivity();
    };

    beat();
    const timer = setInterval(beat, PING_INTERVAL_MS);
    document.addEventListener('visibilitychange', beat);

    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', beat);
    };
  }, [isAuthenticated]);

  const value = {
    user,
    isAuthenticated,
    loading,
    login,
    logout,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
